import { AppendReason, ErrorMessage, LongPropertyTextBox, MList, MListGeneric, QueryBuilder, RemoveReason, StringPropertyTextArea, StringRealProperty, ViewUpdate, VList, VObject, VObjectInList, VObjectInListWithParent } from "@1oo4/miv";
import { VSectionEdit } from "./views";
import { MContent, MPicture, MText, MCell, MCellContent, MCellPicture, MCellText, MColumn, MTable, MCode, CodeLanguage, MContentWithText, MChapter, MSection, MArticle, MCodeTab } from "../../shared/model/mdoc";
import { handleError, runAsync } from "../../shared/pages";
import { DocClient } from "../../shared/docclient";
import { VRefArticle, VRefChapter, VRefSection } from "./refs";
import { PictureManager } from "../../shared/uitools/picturetools";

export abstract class VContentEdit extends VObjectInListWithParent<VSectionEdit> {

    readonly view: HTMLDivElement;
    private readonly bCS: HTMLInputElement | undefined;
    private readonly bTS: HTMLInputElement | undefined;
    private event_enable: boolean = false;

    constructor(m: MContent, section: VSectionEdit) {
        super(m, section);
        const template = document.getElementById("tpl_edit_content") as HTMLTemplateElement
        this.view = (template.content.cloneNode(true) as DocumentFragment).children[0] as HTMLDivElement;

        const bDelete = this.view.querySelector(".edit_delete") as HTMLButtonElement;
        bDelete.onclick = async () => {
            if (window.confirm("do you want to delete this element and its content ?")) {
                const error: ErrorMessage | undefined = await runAsync(m.DeleteAsync());
                handleError(error);
            };
        }

        const bUp = this.view.querySelector(".edit_up") as HTMLButtonElement;
        bUp.onclick = async () => {
            const error: ErrorMessage | undefined = await runAsync(m.MoveUpAsync());
            handleError(error);
        }
        const bDown = this.view.querySelector(".edit_down") as HTMLButtonElement;
        bDown.onclick = async () => {
            const error: ErrorMessage | undefined = await runAsync(m.MoveDownAsync());
            handleError(error);
        }

        const inputs = this.view.querySelectorAll('input[type="checkbox"]');
        const self = this;
        for (let i = 0; i < inputs.length; i++) {
            const x = inputs[i] as HTMLInputElement;
            switch (x.value) {
                case "cs": this.bCS = (x as HTMLInputElement);
                case "ts": this.bTS = (x as HTMLInputElement);
            }
            x.addEventListener('change', (genericEvt: Event) => self.langFlagChange(genericEvt));
        }

        this.event_enable = true;
    }

    abstract getModel(): MContent;

    async langFlagChange(evt: Event) {
        if (this.event_enable) {
            this.event_enable = false;
            const input = evt.currentTarget as HTMLInputElement;
            if (input.checked) {
                const lang = (input == this.bCS) ? CodeLanguage.CS : CodeLanguage.TS;
                const error = await runAsync(this.getModel().LanguageFlag.SetValueAsync(lang));
                handleError(error);
            } else {
                const error = await runAsync(this.getModel().LanguageFlag.SetValueAsync(CodeLanguage.PSEUDO));
                handleError(error);
            }
            this.event_enable = true;
        }
    }

    protected addDetail(detail: HTMLElement): void {
        (this.view.querySelector(".edit_detail_container") as HTMLDivElement).appendChild(detail);
    }

    protected ModelUpdated(vu: ViewUpdate): void {
        const m = this.getModel();
        if (vu.IsChanged(m.LanguageFlag)) {
            this.event_enable = false;
            if (m.LanguageFlag.Value == CodeLanguage.CS) {
                if (this.bCS) this.bCS.checked = true;
                if (this.bTS) this.bTS.checked = false;

            } else if (m.LanguageFlag.Value == CodeLanguage.TS) {
                if (this.bCS) this.bCS.checked = false;
                if (this.bTS) this.bTS.checked = true;
            } else {
                if (this.bCS) this.bCS.checked = false;
                if (this.bTS) this.bTS.checked = false;
            }
            this.event_enable = true;
        }
        this.ContentUpdated(vu);
    }
    protected abstract ContentUpdated(vu: ViewUpdate): void;

    protected AppendToGUI(ar: AppendReason, vu: ViewUpdate): void {
        this.Parent.panelContent.appendChild(this.view);
    }
    protected InsertIntoGUI(index: number, vu: ViewUpdate): void {
        this.Parent.panelContent.insertBefore(this.view, this.Parent.panelContent.children[index]);
    }
    protected RemoveFromGUI(rr: RemoveReason, vu: ViewUpdate): void {
        this.Parent.panelContent.removeChild(this.view);
    }

}

function makeTextContentEdit(v: VObject, prop: StringRealProperty, content: MContentWithText, allProps: StringRealProperty[] | undefined): StringPropertyTextArea {
    const area = StringPropertyTextArea.CreateWithContainer();
    area.Setup(prop, v);
    area.CustomSetter = (txt: string | undefined, qb: QueryBuilder) => {
        (content.Client as DocClient).richParser.setText(prop, content, qb, txt, allProps);
        return undefined;
    }
    return area;
}

export abstract class VContentWithTextEdit extends VContentEdit {
    private static default_display: string = "";
    private readonly textrefs: HTMLDivElement;
    private readonly cPart: HTMLDivElement;
    readonly chaptersPanel: HTMLDivElement;
    private readonly aPart: HTMLDivElement;
    readonly articlesPanel: HTMLDivElement;
    private readonly sPart: HTMLDivElement;
    readonly sectionsPanel: HTMLDivElement;
    private readonly chapters: VList<VRefChapter, MChapter>;
    private readonly articles: VList<VRefArticle, MArticle>;
    private readonly sections: VList<VRefSection, MSection>;

    constructor(m: MContentWithText, section: VSectionEdit) {
        super(m, section);
        const template = document.getElementById("tpl_edit_textrefs") as HTMLTemplateElement
        this.textrefs = (template.content.cloneNode(true) as DocumentFragment).children[0] as HTMLDivElement;

        this.cPart = this.textrefs.querySelector(".chapter_ref_part") as HTMLDivElement;
        this.chaptersPanel = this.cPart.querySelector(".dockpanel_row_stretch") as HTMLDivElement;
        this.aPart = this.textrefs.querySelector(".article_ref_part") as HTMLDivElement;
        this.articlesPanel = this.aPart.querySelector(".dockpanel_row_stretch") as HTMLDivElement;
        this.sPart = this.textrefs.querySelector(".section_ref_part") as HTMLDivElement;
        this.sectionsPanel = this.sPart.querySelector(".dockpanel_row_stretch") as HTMLDivElement;
        if (VContentWithTextEdit.default_display.length <= 0) {
            VContentWithTextEdit.default_display = this.cPart.style.display;
        }

        this.chapters = new VList(m.ChapterRefs, this, x => new VRefChapter(x, this));
        this.articles = new VList(m.ArticleRefs, this, x => new VRefArticle(x, this));
        this.sections = new VList(m.SectionRefs, this, x => new VRefSection(x, this));
    }
    getModel(): MContentWithText {
        return this.GetModelWithText();
    }
    protected abstract GetModelWithText(): MContentWithText;

    protected AddDetailText(html: HTMLElement) {
        const container = this.textrefs.querySelector(".container") as HTMLDivElement;
        container.appendChild(html);
        this.addDetail(this.textrefs);
    }

    protected ContentUpdated(vu: ViewUpdate): void {
        const model = this.GetModelWithText();
        VContentWithTextEdit.updatePart(this.cPart, vu, model.ChapterRefs);
        VContentWithTextEdit.updatePart(this.aPart, vu, model.ArticleRefs);
        VContentWithTextEdit.updatePart(this.sPart, vu, model.SectionRefs);
        this.ContentWithTextUpdated(vu);
    }
    protected abstract ContentWithTextUpdated(vu: ViewUpdate): void
    private static updatePart(part: HTMLDivElement, vu: ViewUpdate, list: MListGeneric) {
        if (vu.IsChanged(list)) {
            if (list.Count > 0) {
                part.style.display = VContentWithTextEdit.default_display;
            } else {
                part.style.display = "none";
            }
        }
    }
}

export class VTextEdit extends VContentWithTextEdit {

    private readonly mText: MText;
    private readonly text: StringPropertyTextArea;

    constructor(m: MText, section: VSectionEdit) {
        super(m, section);
        this.mText = m;
        this.text = makeTextContentEdit(this, m.Text, m, undefined);
        this.AddDetailText(this.text.Container);
    }
    protected GetModelWithText(): MContentWithText {
        return this.mText;
    }

    protected ContentWithTextUpdated(vu: ViewUpdate): void {
        // rien
    }
}

export class VPictureEdit extends VContentEdit {

    private readonly mPicture: MPicture;
    private readonly img: PictureManager;

    constructor(m: MPicture, section: VSectionEdit) {
        super(m, section);
        this.mPicture = m;
        this.img = new PictureManager();
        this.addDetail(this.img.image);

        const buttonsPanel = this.view.querySelector(".uniform_column_buttons") as HTMLDivElement;
        const bImg = document.createElement("button");
        bImg.textContent = "🖼";
        buttonsPanel.appendChild(bImg);
        bImg.onclick = () => {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "image/png, image/jpeg";

            input.onchange = async () => {
                const file = input.files?.[0];
                if (!file) {
                    return;
                }
                const arrayBuffer = await runAsync(file.arrayBuffer());
                const bytes = new Uint8Array(arrayBuffer);
                const error = await runAsync(m.Data.SetValueAsync(bytes));
                handleError(error);
            };

            input.click();
        }
    }
    getModel(): MPicture {
        return this.mPicture;
    }

    protected ContentUpdated(vu: ViewUpdate): void {
        if (vu.IsChanged(this.mPicture.Data)) {
            this.img.setPicture(this.mPicture.Data);
        }
    }
}


export class VCodeEdit extends VContentEdit {

    private readonly mCode: MCode;
    readonly panelTabs: HTMLDivElement;
    private readonly tabs: VList<VCodeTabEdit, MCodeTab>;

    constructor(m: MCode, section: VSectionEdit) {
        super(m, section);
        this.mCode = m;
        const detail = ((document.getElementById('tpl_edit_code') as HTMLTemplateElement).content.cloneNode(true) as DocumentFragment).children[0] as HTMLDivElement;
        this.addDetail(detail);
        this.panelTabs = detail.querySelector('.edit_code_tab_container') as HTMLDivElement;
        this.tabs = new VList(m.Tabs, this, x => new VCodeTabEdit(x, this));

        const bAdd = detail.querySelector('button');
        if (bAdd) {
            bAdd.onclick = async () => {
                const created = await runAsync(m.AppendTabAsync());
                if (created === undefined) {
                    handleError(m.Client.Error, true);
                }
            }
        }
    }
    getModel(): MCode {
        return this.mCode;
    }

    protected ContentUpdated(vu: ViewUpdate): void {
        // rien
    }
}

type InputLanguage = HTMLInputElement & { language: CodeLanguage };

class VCodeTabEdit extends VObjectInList<MCodeTab, VCodeEdit> {

    private readonly view: HTMLDivElement;
    private readonly code: StringPropertyTextArea;
    private readonly pseudo!: InputLanguage;
    private readonly cs!: InputLanguage;
    private readonly ts!: InputLanguage;
    private readonly xaml!: InputLanguage;
    private readonly html!: InputLanguage;
    private readonly css!: InputLanguage;
    private readonly json!: InputLanguage;
    private readonly sql!: InputLanguage;
    private readonly radios: Array<InputLanguage> = new Array();
    private evt_enabled: boolean = false;

    constructor(m: MCodeTab, parent: VCodeEdit) {
        super(m, parent);

        this.view = ((document.getElementById("tpl_edit_code_tab") as HTMLTemplateElement).content.cloneNode(true) as DocumentFragment).children[0] as HTMLDivElement;

        const bDelete = this.view.querySelector("button");
        if (bDelete) {
            bDelete.onclick = async () => {
                if (window.confirm("do you want to delete this element and its content ?")) {
                    const error = await runAsync(this.Model.DeleteAsync());
                    handleError(error);
                }
            }
        }

        this.code = StringPropertyTextArea.CreateWithContainer(true, false)
        this.code.Setup(m.Code, this);
        const codeContainer = this.view.querySelector(".edit_code") as HTMLDivElement;
        codeContainer.appendChild(this.code.Container);

        const self = this;
        const inputs = this.view.querySelectorAll<HTMLInputElement>('input[type="radio"]');
        for (let i = 0; i < inputs.length; i++) {
            const x = inputs[i] as InputLanguage;
            let lan = CodeLanguage.PSEUDO;
            switch (x.value) {
                case "pseudo":
                    this.pseudo = x;
                    break;
                case "cs":
                    this.cs = x;
                    lan = CodeLanguage.CS;
                    break;
                case "ts":
                    this.ts = x;
                    lan = CodeLanguage.TS;
                    break;
                case "xaml":
                    this.xaml = x;
                    lan = CodeLanguage.XAML;
                    break;
                case "html":
                    this.html = x;
                    lan = CodeLanguage.HTML;
                    break;
                case "css":
                    this.css = x;
                    lan = CodeLanguage.CSS;
                    break;
                case "json":
                    this.json = x;
                    lan = CodeLanguage.JSON;
                    break;
                case "sql":
                    this.sql = x;
                    lan = CodeLanguage.SQL;
                    break;
            }
            x.language = lan;
            x.id = x.value + m.ID;
            x.name = "language" + m.ID;
            x.addEventListener('change', (genericEvt: Event) => self.radioChange(genericEvt));
            this.radios.push(x);
        }
        const labels = this.view.querySelectorAll<HTMLLabelElement>('label');
        for (let i = 0; i < labels.length; i++) {
            const x = labels[i];
            x.htmlFor = x.htmlFor + m.ID;
        }

        this.evt_enabled = true;
    }

    private async radioChange(genericEvt: Event) {
        if (this.evt_enabled) {
            this.evt_enabled = false;
            const input = genericEvt.currentTarget as InputLanguage;
            if (input.checked) {
                const error = await runAsync(this.Model.Language.SetValueAsync(input.language));
                handleError(error);
            }
            this.evt_enabled = true;
        }
    }

    protected ModelUpdated(vu: ViewUpdate): void {
        if (vu.IsChanged(this.Model.Language)) {
            const remember_enabled = this.evt_enabled;
            this.evt_enabled = false;
            const newVal = this.Model.Language.Value;
            let found = false;
            for (let i = 0; i < this.radios.length; i++) {
                const x = this.radios[i];
                if (x.language == newVal) {
                    x.checked = true;
                    found = true;
                    break;
                }
            }
            if (!found) {
                this.pseudo.checked = true;
            }
            this.evt_enabled = remember_enabled;
        }
    }

    protected AppendToGUI(ar: AppendReason, vu: ViewUpdate): void {
        this.Parent.panelTabs.appendChild(this.view);
    }
    protected InsertIntoGUI(index: number, vu: ViewUpdate): void {
        this.Parent.panelTabs.insertBefore(this.view, this.Parent.panelTabs.children[index]);
    }
    protected RemoveFromGUI(rr: RemoveReason, vu: ViewUpdate): void {
        this.Parent.panelTabs.removeChild(this.view);
    }
}

export class VTableEdit extends VContentWithTextEdit {

    private readonly mTable: MTable;
    private readonly grid: HTMLDivElement;
    private readonly cols: VList<VColumn, MColumn>;
    private readonly dummy: HTMLDivElement;
    private readonly bDeleteRow: Array<HTMLButtonElement>;
    private last_cols: number = 0;
    private last_build: number = 0;

    constructor(m: MTable, section: VSectionEdit) {
        super(m, section);
        this.mTable = m;

        const template = document.getElementById("tpl_edit_table") as HTMLTemplateElement
        const detail = (template.content.cloneNode(true) as DocumentFragment).children[0] as HTMLDivElement;
        this.AddDetailText(detail);

        const bAddRow = detail.querySelector(".edit_table_add_row") as HTMLDivElement;
        bAddRow.onclick = async () => {
            const error = await runAsync(m.AppendRowAsync())
            handleError(error);
        }
        const bAddCol = detail.querySelector(".edit_table_add_col") as HTMLDivElement;
        bAddCol.onclick = async () => {
            const created = await runAsync(m.AppendColumnAsync());
            if (created === undefined) {
                handleError(m.Client.Error, true);
            }
        }

        this.grid = detail.querySelector(".table_grid") as HTMLDivElement;
        this.cols = new VList(m.Columns, this, x => new VColumn(x, this));

        this.dummy = document.createElement("div");
        this.bDeleteRow = new Array();
    }
    protected GetModelWithText(): MContentWithText {
        return this.mTable;
    }

    protected ContentWithTextUpdated(vu: ViewUpdate): void {
        if (vu.IsChanged(this.mTable.Rows) || vu.IsChanged(this.mTable.Columns)) {
            this.buildTable(vu);
        }
    }

    setupColumns(vu: ViewUpdate) {
        if (this.last_cols != vu.Counter) {
            this.last_cols = vu.Counter;
            let format: string = "auto";
            for (let i = 0; i < this.cols.Count; i++) {
                const w = this.cols.Get(i).Model.Width.Value;
                if (w > 0) {
                    format = format + " " + w + "fr";
                } else {
                    format = format + " auto";
                }
            }
            this.grid.style.gridTemplateColumns = format;
        }
    }

    buildTable(vu: ViewUpdate) {
        if (this.last_build != vu.Counter) {
            this.last_build = vu.Counter;
            while (this.grid.children.length > 0) {
                this.grid.removeChild(this.grid.childNodes.item(this.grid.children.length - 1));
            }
            this.setupColumns(vu);
            this.grid.appendChild(this.dummy);
            for (let c = 0; c < this.cols.Count; c++) {
                this.grid.appendChild(this.cols.Get(c).header);
            }
            const rows = this.mTable.Rows.Count;
            for (let i = 0; i < rows; i++) {
                let bDel: HTMLButtonElement;
                if (i < this.bDeleteRow.length) {
                    bDel = this.bDeleteRow[i];
                } else {
                    bDel = document.createElement("button");
                    this.bDeleteRow.push(bDel);
                    bDel.innerHTML = "❌";
                    bDel.onclick = () => this.bDelClick(i);
                }
                this.grid.appendChild(bDel);
                for (let c = 0; c < this.cols.Count; c++) {
                    this.grid.appendChild(this.cols.Get(c).cells.Get(i).view);
                }
            }
        }
    }

    private async bDelClick(index: number) {
        const error = await runAsync(this.mTable.DeleteRowAsync(index));
        handleError(error);
    }
}

class VColumn extends VObjectInList<MColumn, VTableEdit> {

    readonly cells: VList<VCellEdit, MCell>;
    readonly header: HTMLDivElement;

    constructor(m: MColumn, table: VTableEdit) {
        super(m, table);
        this.cells = new VList(m.Cells, this, x => new VCellEdit(x, this));
        const template = document.getElementById("tpl_edit_column") as HTMLTemplateElement
        this.header = (template.content.cloneNode(true) as DocumentFragment).children[0] as HTMLDivElement;
        const width = this.header.querySelector(".dockpanel_row_stretch") as HTMLInputElement;
        const widthInput = new LongPropertyTextBox(width);
        widthInput.Setup(m.Width, this);
        const bDelete = this.header.querySelector(".edit_delete") as HTMLButtonElement;
        bDelete.onclick = async () => {
            const error = await runAsync(m.DeleteAsync());
            handleError(error);
        }
    }

    protected ModelUpdated(vu: ViewUpdate): void {
        if (vu.IsChanged(this.Model.Width)) {
            this.Parent.setupColumns(vu);
        }
    }

    protected AppendToGUI(ar: AppendReason, vu: ViewUpdate): void {
        this.Parent.buildTable(vu);
    }
    protected RemoveFromGUI(rr: RemoveReason, vu: ViewUpdate): void {
        this.Parent.buildTable(vu);
    }
    protected InsertIntoGUI(index: number, vu: ViewUpdate): void {
        this.Parent.buildTable(vu);
    }
}

export class VCellEdit extends VObjectInList<MCell, VColumn> {

    private readonly contents: VList<VCellContentEdit, MCellContent>;
    readonly view: HTMLDivElement;
    readonly panelContents: HTMLDivElement;

    constructor(m: MCell, col: VColumn) {
        super(m, col);
        this.contents = new VList(m.Content, this, x => x.makeViewEdit(this));
        this.panelContents = document.createElement("div");
        this.view = this.panelContents;
    }
    protected AppendToGUI(ar: AppendReason, vu: ViewUpdate): void {
        this.Parent.Parent.buildTable(vu);
    }
    protected RemoveFromGUI(rr: RemoveReason, vu: ViewUpdate): void {
        this.Parent.Parent.buildTable(vu);
    }
    protected InsertIntoGUI(index: number, vu: ViewUpdate): void {
        this.Parent.Parent.buildTable(vu);
    }
}

export abstract class VCellContentEdit extends VObjectInList<MCellContent, VCellEdit> {
    constructor(m: MCellContent, cell: VCellEdit) {
        super(m, cell);
    }
}

export class VCellTextEdit extends VCellContentEdit {

    private readonly text: StringPropertyTextArea;

    constructor(m: MCellText, cell: VCellEdit) {
        super(m, cell);
        const table = m.Parent.Parent.Parent;
        const allProps: Array<StringRealProperty> = new Array();
        for (let i = 0; i < table.Columns.Count; i++) {
            const col = table.Columns.Get(i);
            for (let j = 0; j < col.Cells.Count; j++) {
                const cell = col.Cells.Get(j);
                for (let k = 0; k < cell.Content.Count; k++) {
                    cell.Content.Get(k).addRichTextToList(allProps);
                }
            }
        }
        this.text = makeTextContentEdit(this, m.Text, table, allProps);
    }

    protected AppendToGUI(ar: AppendReason, vu: ViewUpdate): void {
        this.Parent.panelContents.appendChild(this.text.Container);
    }
    protected RemoveFromGUI(rr: RemoveReason, vu: ViewUpdate): void {
        this.Parent.panelContents.removeChild(this.text.Container);
    }
    protected InsertIntoGUI(index: number, vu: ViewUpdate): void {
        this.Parent.panelContents.insertBefore(this.text.Container, this.Parent.panelContents.children[index]);
    }
}

export class VCellPictureEdit extends VCellContentEdit {
    constructor(m: MCellPicture, cell: VCellEdit) {
        super(m, cell);
    }
    protected AppendToGUI(ar: AppendReason, vu: ViewUpdate): void {
        // TODO
    }
    protected RemoveFromGUI(rr: RemoveReason, vu: ViewUpdate): void {
        // TODO
    }
    protected InsertIntoGUI(index: number, vu: ViewUpdate): void {
        // TODO
    }
}